/** Pengajuan-judul lifecycle: create, review, promote, reject. */

import { createDoc, updateDoc, getById, fetchQuery } from '../service';
import { COL, q, col, where, limit, select } from '../../collections';
import { utcNowIso } from '../../format';
import { DEFAULT_FAKULTAS, stageIndex } from '../../constants';
import { findSimilarJudul, normalizeJudul } from '../../judul';
import { currentPeriode } from './periode';
import type { MahasiswaDoc, PengajuanJudulDoc, SkripsiDoc } from '../../types';

/**
 * Titles already in use for duplicate detection.
 * `exclude` (normalized) lets a resubmit skip its own attempt. By default
 * in-flight submissions (menunggu/revisi) are included too, so two pending
 * attempts with near-identical judul cannot silently coexist — this closes the
 * collision-at-approval gap where a later submission was only caught after one
 * of them was approved. `committedOnly` restricts to PA-approved judul +
 * skripsi titles (used by the kaprodi promotion guard, so the first-come title
 * is never blocked by a later, user-confirmed duplicate).
 */
async function existingJudulCandidates(
  exclude: string[] = [],
  opts: { committedOnly?: boolean } = {},
): Promise<string[]> {
  const excluded = new Set(exclude.map((j) => normalizeJudul(j)));
  // `select` returns partial docs; only `judul` is read below.
  const [skripsi, pengajuan] = await Promise.all([
    fetchQuery<Pick<SkripsiDoc, 'judul' | 'judulKey'>>(
      q(col(COL.skripsi), select('judul', 'judulKey')),
    ),
    fetchQuery<Pick<PengajuanJudulDoc, 'judul' | 'judulKey'>>(
      q(
        col(COL.pengajuanJudul),
        opts.committedOnly
          ? where('status', '==', 'disetujui')
          : where('status', 'in', ['menunggu', 'revisi', 'disetujui']),
        select('judul', 'judulKey'),
      ),
    ),
  ]);
  return [...skripsi.map((s) => s.judul), ...pengajuan.map((p) => p.judul)].filter(
    (j): j is string =>
      typeof j === 'string' && j.trim().length > 0 && !excluded.has(normalizeJudul(j)),
  );
}

/**
 * Reject a judul that collides with an existing one unless the mahasiswa
 * explicitly confirmed the duplicate. Returns the colliding title so callers
 * can surface it. Throws when confirmation is missing.
 */
export async function assertJudulNotDuplicated(
  judul: string,
  exclude: string[] = [],
  confirmDuplicate?: boolean,
  opts: { committedOnly?: boolean } = {},
): Promise<void> {
  const match = findSimilarJudul(await existingJudulCandidates(exclude, opts), judul);
  if (match && !confirmDuplicate) {
    throw new Error(
      `Judul mirip dengan yang sudah dipakai: “${match.title}” — konfirmasi untuk tetap mengajukan.`,
    );
  }
}

/**
 * Create a pengajuan-judul attempt. Standalone — no skripsi is created yet;
 * the attempt lives until a judul is approved by both the Dosen PA and the
 * kaprodi, at which point promotePengajuanToSkripsi creates the skripsi.
 */
export async function createPengajuanJudul(input: {
  mahasiswaId: string;
  dosenPaId: string;
  judul: string;
  judulAlternatif?: string[];
  /** Calon pembimbing yang diusulkan mahasiswa (maks. 3, SOP II.9). */
  calonPembimbing?: string[];
  abstrak?: string;
  actorName?: string;
  /** Set when the mahasiswa explicitly confirmed a near-duplicate title. */
  confirmDuplicate?: boolean;
}) {
  await assertJudulNotDuplicated(input.judul, [], input.confirmDuplicate);
  if ((input.calonPembimbing?.length ?? 0) > 3) {
    throw new Error('Maksimal 3 calon pembimbing.');
  }

  const mhsQuery = q(col(COL.mahasiswa), where('userId', '==', input.mahasiswaId), limit(1));
  const mhs = (await fetchQuery<MahasiswaDoc>(mhsQuery))[0];

  const now = utcNowIso();
  const doc = await createDoc<PengajuanJudulDoc>(COL.pengajuanJudul, {
    mahasiswaId: input.mahasiswaId,
    dosenPaId: input.dosenPaId,
    prodiId: mhs?.prodiId ?? '',
    judul: input.judul,
    judulKey: normalizeJudul(input.judul),
    judulAlternatif: input.judulAlternatif ?? [],
    calonPembimbing: input.calonPembimbing ?? [],
    abstrak: input.abstrak,
    status: 'menunggu',
    history: [
      {
        by: input.mahasiswaId,
        byName: input.actorName,
        at: now,
        action: 'mengajukan judul skripsi untuk review',
        note: input.judul,
      },
    ],
    createdAt: now,
    updatedAt: now,
  });

  return doc;
}

/** Dosen PA reviews the pengajuan: setujui / tolak / saran revisi (logged). */
export async function reviewPengajuanJudul(input: {
  id: string;
  action: 'setujui' | 'tolak' | 'revisi';
  feedback?: string;
  actorId: string;
  actorName?: string;
}) {
  const cur = await getById<PengajuanJudulDoc>(COL.pengajuanJudul, input.id);
  const history = [...(cur?.history ?? [])];
  const now = utcNowIso();
  const actionLabel =
    input.action === 'setujui'
      ? 'menyetujui pengajuan judul'
      : input.action === 'tolak'
        ? 'menolak pengajuan judul'
        : 'meminta revisi judul';
  history.push({
    by: input.actorId,
    byName: input.actorName,
    at: now,
    action: actionLabel,
    note: input.feedback,
  });
  const status =
    input.action === 'setujui' ? 'disetujui' : input.action === 'tolak' ? 'ditolak' : 'revisi';
  return updateDoc<PengajuanJudulDoc>(COL.pengajuanJudul, input.id, {
    status,
    rejectedBy: input.action === 'tolak' ? 'pa' : undefined,
    feedback: input.feedback,
    history,
  });
}

/** Mahasiswa resubmits the judul after a revisi request (same attempt). */
export async function resubmitPengajuanJudul(input: {
  id: string;
  judul: string;
  judulAlternatif?: string[];
  /** Calon pembimbing yang diusulkan mahasiswa (maks. 3, SOP II.9). */
  calonPembimbing?: string[];
  abstrak?: string;
  actorId: string;
  actorName?: string;
  /** Set when the mahasiswa explicitly confirmed a near-duplicate title. */
  confirmDuplicate?: boolean;
}) {
  const cur = await getById<PengajuanJudulDoc>(COL.pengajuanJudul, input.id);
  await assertJudulNotDuplicated(
    input.judul,
    [cur?.judul ?? '', ...(cur?.judulAlternatif ?? [])],
    input.confirmDuplicate,
  );
  if ((input.calonPembimbing?.length ?? 0) > 3) {
    throw new Error('Maksimal 3 calon pembimbing.');
  }
  const history = [...(cur?.history ?? [])];
  history.push({
    by: input.actorId,
    byName: input.actorName,
    at: utcNowIso(),
    action: 'mengajukan ulang judul setelah revisi',
    note: input.judul,
  });
  return updateDoc<PengajuanJudulDoc>(COL.pengajuanJudul, input.id, {
    judul: input.judul,
    judulKey: normalizeJudul(input.judul),
    judulAlternatif: input.judulAlternatif ?? [],
    calonPembimbing: input.calonPembimbing ?? [],
    abstrak: input.abstrak,
    status: 'menunggu',
    rejectedBy: undefined,
    feedback: undefined,
    submittedToKaprodi: false,
    history,
  });
}

/** Mark the pengajuan as forwarded to kaprodi (awaiting prodi decision). */
export async function markPengajuanSubmittedToKaprodi(id: string) {
  return updateDoc<PengajuanJudulDoc>(COL.pengajuanJudul, id, { submittedToKaprodi: true });
}

/**
 * Kaprodi approves an already PA-approved judul: promotes it into a skripsi at
 * stage `penetapan_pembimbing` and links the attempt to it.
 */
export async function promotePengajuanToSkripsi(input: {
  id: string;
  kaprodiId: string;
  kaprodiName?: string;
  actorId: string;
  actorName?: string;
  /** Kaprodi's structured judul verification (SOP I.6) — all three must be true. */
  kaprodiVerifikasi: {
    kesesuaianBidang: boolean;
    kelayakanKebaruan: boolean;
    ketersediaanCalonPembimbing: boolean;
  };
}) {
  const cur = await getById<PengajuanJudulDoc>(COL.pengajuanJudul, input.id);
  if (!cur || cur.status !== 'disetujui') return;

  const { kesesuaianBidang, kelayakanKebaruan, ketersediaanCalonPembimbing } =
    input.kaprodiVerifikasi;
  if (!kesesuaianBidang || !kelayakanKebaruan || !ketersediaanCalonPembimbing) {
    throw new Error(
      'Verifikasi kaprodi belum lengkap: kesesuaian bidang, kelayakan/kebaruan topik, dan ketersediaan calon pembimbing harus disetujui.',
    );
  }

  // Finalizing a judul into a skripsi is the commit point for duplicate titles:
  // block promotion only against committed titles (existing skripsi + PA-approved
  // judul). This preserves first-come semantics even when the mahasiswa had to
  // confirm a near-duplicate earlier.
  await assertJudulNotDuplicated(
    cur.judul,
    [cur.judul, ...(cur.judulAlternatif ?? [])],
    undefined,
    { committedOnly: true },
  );

  const mhsQuery = q(col(COL.mahasiswa), where('userId', '==', cur.mahasiswaId), limit(1));
  const mhs = (await fetchQuery<MahasiswaDoc>(mhsQuery))[0];

  const now = utcNowIso();
  const p = await currentPeriode();
  const skripsi = await createDoc<SkripsiDoc>(COL.skripsi, {
    mahasiswaId: cur.mahasiswaId,
    judul: cur.judul,
    judulKey: normalizeJudul(cur.judul),
    abstrak: cur.abstrak,
    judulAlternatif: cur.judulAlternatif,
    calonPembimbing: cur.calonPembimbing,
    stage: 'penetapan_pembimbing',
    status: 'active',
    prodi: mhs?.prodi ?? '',
    prodiId: mhs?.prodiId ?? cur.prodiId,
    jurusan: mhs?.jurusan ?? '',
    jurusanId: mhs?.jurusanId,
    fakultas: mhs?.fakultas || DEFAULT_FAKULTAS,
    stageTimeline: {
      penetapan_pembimbing: {
        at: now,
        tahunAjaran: p.tahunAjaran,
        semester: p.semester,
        index: stageIndex('penetapan_pembimbing'),
      },
    },
    createdAt: now,
    updatedAt: now,
  });

  const history = [...(cur.history ?? [])];
  history.push({
    by: input.actorId,
    byName: input.actorName ?? input.kaprodiName,
    at: now,
    action: 'kaprodi menyetujui judul dan menetapkan skripsi',
    note: cur.judul,
  });
  await updateDoc<PengajuanJudulDoc>(COL.pengajuanJudul, cur.id, {
    status: 'disetujui',
    kaprodiId: input.kaprodiId,
    kaprodiVerifikasi: { ...input.kaprodiVerifikasi, at: now },
    skripsiId: skripsi.id,
    promotedAt: now,
    history,
  });

  return skripsi;
}

/** Kaprodi rejects an already PA-approved judul (re-enables a new attempt). */
export async function rejectPengajuanByKaprodi(input: {
  id: string;
  feedback?: string;
  actorId: string;
  actorName?: string;
}) {
  const cur = await getById<PengajuanJudulDoc>(COL.pengajuanJudul, input.id);
  const history = [...(cur?.history ?? [])];
  history.push({
    by: input.actorId,
    byName: input.actorName,
    at: utcNowIso(),
    action: 'kaprodi menolak pengajuan judul',
    note: input.feedback,
  });
  return updateDoc<PengajuanJudulDoc>(COL.pengajuanJudul, input.id, {
    status: 'ditolak',
    rejectedBy: 'kaprodi',
    feedback: input.feedback,
    submittedToKaprodi: false,
    history,
  });
}
